import { useState, useCallback, useEffect } from 'react'
import { FileText, Paperclip, Download, X, Table, BookOpen, History, BarChart3, Loader2 } from 'lucide-react'
import axios from 'axios'
import Button from '@/components/ui/Button'
import BusinessSelect from '@/components/ui/BusinessSelect'
import Checkbox from '@/components/ui/Checkbox'
import DateInput from '@/components/ui/DateInput'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'
import { OpenAPI } from '@/api'
import { TaskReportService } from '@/api/generated/services/TaskReportService'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'

type ReportFormat = 'hwpx' | 'pdf'

interface ExportItem {
  id: string
  label: string
  description: string
  icon: React.ReactNode
  disabled?: boolean
}

const EXPORT_ITEMS: ExportItem[] = [
  {
    id: 'task',
    label: '과제/업무',
    description: '워크스페이스 전체 과제/업무 데이터를 엑셀로 다운로드합니다.',
    icon: <Table className="w-6 h-6" />,
  },
  {
    id: 'performance',
    label: '성과지표',
    description: '워크스페이스 전체 성과지표 데이터를 엑셀로 다운로드합니다.',
    icon: <BarChart3 className="w-6 h-6" />,
  },
  {
    id: 'report',
    label: '보고서',
    description: '사업별 모든 세부실행과제 보고서를 ZIP으로 다운로드합니다.',
    icon: <FileText className="w-6 h-6" />,
  },
  {
    id: 'attachment',
    label: '증빙자료',
    description: '사업별 모든 증빙자료 파일을 ZIP으로 다운로드합니다.',
    icon: <Paperclip className="w-6 h-6" />,
  },
  {
    id: 'article',
    label: '회의록',
    description: '워크스페이스 전체 회의록을 엑셀로 다운로드합니다.',
    icon: <BookOpen className="w-6 h-6" />,
  },
  {
    id: 'history',
    label: '변경이력',
    description: '변경 이력을 엑셀로 다운로드합니다.',
    icon: <History className="w-6 h-6" />,
  },
]

type ModalType = 'report' | 'attachment' | 'task' | 'performance' | 'article' | 'history' | null
type AttachmentView = 'select' | 'period'

export default function DataExportPage() {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { showSuccess, showError } = useAlert()

  // 모달 상태
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [selectedBusinessName, setSelectedBusinessName] = useState('')

  // 증빙자료 기간 선택
  const [attachmentView, setAttachmentView] = useState<AttachmentView>('select')
  const [periodStartDate, setPeriodStartDate] = useState('')
  const [periodEndDate, setPeriodEndDate] = useState('')

  // 보고서 다운로드 옵션 — 유형 미선택 시 활성 템플릿 전체, 형식 기본값 hwpx
  const [selectedTemplateKeys, setSelectedTemplateKeys] = useState<string[]>([])
  const [reportFormat, setReportFormat] = useState<ReportFormat>('hwpx')
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false)
  const [templatesError, setTemplatesError] = useState<string | null>(null)

  // 다운로드 상태
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState('')

  const resetModal = useCallback(() => {
    setActiveModal(null)
    setSelectedBusinessId('')
    setSelectedBusinessName('')
    setAttachmentView('select')
    setPeriodStartDate('')
    setPeriodEndDate('')
    setSelectedTemplateKeys([])
    setReportFormat('hwpx')
  }, [])

  const handleItemClick = useCallback((itemId: string) => {
    setActiveModal(itemId as ModalType)
    setSelectedBusinessId('')
    setSelectedBusinessName('')
    setAttachmentView('select')
    setPeriodStartDate('')
    setPeriodEndDate('')
    setSelectedTemplateKeys([])
    setReportFormat('hwpx')
  }, [])

  // 보고서 모달에서 사업이 선택되면 해당 사업의 유효 보고서 유형 목록 로드
  useEffect(() => {
    if (activeModal !== 'report' || !selectedBusinessId || !selectedWorkspace?.id) {
      setReportTemplates([])
      setTemplatesError(null)
      return
    }
    let cancelled = false
    setIsTemplatesLoading(true)
    setTemplatesError(null)
    // 사업이 바뀌면 이전 사업 기준 선택은 초기화
    setSelectedTemplateKeys([])

    TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
      workspaceId: selectedWorkspace.id,
      businessId: selectedBusinessId,
    })
      .then(res => {
        if (!cancelled) setReportTemplates(res ?? [])
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load report templates:', err)
        setTemplatesError('보고서 유형 목록을 불러오는데 실패했습니다.')
      })
      .finally(() => {
        if (!cancelled) setIsTemplatesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeModal, selectedBusinessId, selectedWorkspace?.id])

  const toggleTemplateKey = useCallback((key: string) => {
    setSelectedTemplateKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }, [])

  const handleBusinessSelect = useCallback((businessId: string, businessName: string) => {
    setSelectedBusinessId(businessId)
    setSelectedBusinessName(businessName)
  }, [])

  // 보고서 다운로드
  const handleReportDownload = useCallback(async () => {
    if (!selectedBusinessId || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('보고서를 생성하는 중...')

    try {
      // report_template_key는 반복 지정으로 복수 선택 (미선택 시 활성 템플릿 전체)
      const query = new URLSearchParams()
      selectedTemplateKeys.forEach(key => query.append('report_template_key', key))
      query.append('format', reportFormat)

      const response = await axios.get(
        `${OpenAPI.BASE}/api/task/report/by-business/${selectedBusinessId}?${query.toString()}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedBusinessName}_보고서.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess(`"${selectedBusinessName}" 보고서 다운로드가 완료되었습니다.`)
      resetModal()
    } catch (error: any) {
      console.error('보고서 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError(`"${selectedBusinessName}"에 다운로드할 보고서가 없습니다.`)
      } else {
        handleApiError(error, showError, '보고서 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedBusinessId, selectedBusinessName, selectedTemplateKeys, reportFormat, isDownloading, resetModal, showSuccess, showError])

  // 증빙자료 다운로드 (일괄)
  const handleAttachmentDownloadAll = useCallback(async () => {
    if (!selectedBusinessId || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('압축 파일을 준비하는 중...')
    setActiveModal(null)

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/file/by-business/${selectedBusinessId}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedBusinessName}_증빙자료.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess(`"${selectedBusinessName}" 증빙자료 다운로드가 완료되었습니다.`)
      resetModal()
    } catch (error: any) {
      console.error('증빙자료 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError(`"${selectedBusinessName}"에 다운로드할 증빙자료가 없습니다.`)
      } else {
        handleApiError(error, showError, '증빙자료 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedBusinessId, selectedBusinessName, isDownloading, resetModal, showSuccess, showError])

  // 증빙자료 다운로드 (기간별)
  const handleAttachmentDownloadPeriod = useCallback(async () => {
    if (!selectedBusinessId || !periodStartDate || !periodEndDate || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('압축 파일을 준비하는 중...')
    setActiveModal(null)

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/file/by-business/${selectedBusinessId}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: {
            start_date: periodStartDate,
            end_date: periodEndDate,
          },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedBusinessName}_증빙자료_${periodStartDate}_${periodEndDate}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess(`"${selectedBusinessName}" 기간별 증빙자료 다운로드가 완료되었습니다.`)
      resetModal()
    } catch (error: any) {
      console.error('기간별 증빙자료 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError(`"${selectedBusinessName}"에 해당 기간의 증빙자료가 없습니다.`)
      } else {
        handleApiError(error, showError, '증빙자료 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedBusinessId, selectedBusinessName, periodStartDate, periodEndDate, isDownloading, resetModal, showSuccess, showError])

  // 과제/업무 엑셀 다운로드
  const handleTaskExcelDownload = useCallback(async () => {
    if (!selectedWorkspace?.id || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('엑셀 데이터를 생성하는 중...')

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/task/excel/download`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: {
            workspace_id: selectedWorkspace.id,
          },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedWorkspace.name || '워크스페이스'}_과제업무.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('과제/업무 엑셀 다운로드가 완료되었습니다.')
      resetModal()
    } catch (error: any) {
      console.error('과제/업무 엑셀 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError('다운로드할 과제/업무 데이터가 없습니다.')
      } else {
        handleApiError(error, showError, '과제/업무 엑셀 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedWorkspace, isDownloading, resetModal, showSuccess, showError])

  // 성과지표 엑셀 다운로드
  const handlePerformanceExcelDownload = useCallback(async () => {
    if (!selectedWorkspace?.id || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('성과지표 데이터를 생성하는 중...')

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/performance/excel/download`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: { workspace_id: selectedWorkspace.id },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedWorkspace.name || '워크스페이스'}_성과지표.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('성과지표 엑셀 다운로드가 완료되었습니다.')
      resetModal()
    } catch (error: any) {
      console.error('성과지표 엑셀 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError('다운로드할 성과지표 데이터가 없습니다.')
      } else {
        handleApiError(error, showError, '성과지표 엑셀 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedWorkspace, isDownloading, resetModal, showSuccess, showError])

  // 회의록 엑셀 다운로드
  const handleArticleExcelDownload = useCallback(async () => {
    if (!selectedWorkspace?.id || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('회의록 데이터를 생성하는 중...')

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/article/excel/download`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: { workspace_id: selectedWorkspace.id },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedWorkspace.name || '워크스페이스'}_회의록.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('회의록 엑셀 다운로드가 완료되었습니다.')
      resetModal()
    } catch (error: any) {
      console.error('회의록 엑셀 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError('다운로드할 회의록 데이터가 없습니다.')
      } else {
        handleApiError(error, showError, '회의록 엑셀 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedWorkspace, isDownloading, resetModal, showSuccess, showError])

  // 변경이력 엑셀 다운로드
  const handleHistoryExcelDownload = useCallback(async () => {
    if (!selectedWorkspace?.id || isDownloading) return

    setIsDownloading(true)
    setDownloadProgress('변경이력 데이터를 생성하는 중...')

    try {
      const response = await axios.get(
        `${OpenAPI.BASE}/api/history/action/excel/download`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: {
            ...(OpenAPI.TOKEN && { 'Authorization': `Bearer ${OpenAPI.TOKEN}` }),
          },
          params: {
            org_id: selectedWorkspace.org_id,
            workspace_id: selectedWorkspace.id,
          },
        }
      )

      setDownloadProgress('파일을 다운로드하는 중...')

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${selectedWorkspace.name || '워크스페이스'}_변경이력.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('변경이력 엑셀 다운로드가 완료되었습니다.')
      resetModal()
    } catch (error: any) {
      console.error('변경이력 엑셀 다운로드 실패:', error)
      if (error?.response?.status === 404) {
        showError('다운로드할 변경이력 데이터가 없습니다.')
      } else {
        handleApiError(error, showError, '변경이력 엑셀 다운로드에 실패했습니다.')
      }
    } finally {
      setIsDownloading(false)
      setDownloadProgress('')
    }
  }, [selectedWorkspace, isDownloading, resetModal, showSuccess, showError])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Download Progress */}
      {isDownloading && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-lg mt-4"
          style={{ backgroundColor: designTokens.colors.gray[0] }}
        >
          <div
            className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: designTokens.colors.primary.DEFAULT, borderTopColor: 'transparent' }}
          />
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[6],
            }}
          >
            {downloadProgress || '다운로드를 준비하는 중...'}
          </span>
        </div>
      )}

      {/* Export Items Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {EXPORT_ITEMS.map(item => (
          <button
            key={item.id}
            type="button"
            onClick={() => !item.disabled && handleItemClick(item.id)}
            disabled={isDownloading || item.disabled}
            className={`flex flex-col items-start p-5 border rounded-lg text-left transition-all ${item.disabled ? 'cursor-not-allowed' : 'hover:shadow-md'}`}
            style={{
              borderColor: designTokens.colors.gray[1],
              fontFamily: designTokens.typography.fontFamily.sans[0],
              opacity: isDownloading || item.disabled ? 0.5 : 1,
            }}
          >
            <div
              className="p-2.5 rounded-lg mb-3"
              style={{
                backgroundColor: designTokens.colors.gray[0],
                color: designTokens.colors.primary.DEFAULT,
              }}
            >
              {item.icon}
            </div>
            <div className="flex items-center gap-2">
              <span
                style={{
                  fontSize: designTokens.typography.fontSize['body-large'][0],
                  fontWeight: 600,
                  color: designTokens.colors.gray[6],
                }}
              >
                {item.label}
              </span>
              {item.disabled && (
                <span
                  className="px-1.5 py-0.5 rounded text-xs"
                  style={{
                    backgroundColor: designTokens.colors.gray[1],
                    color: designTokens.colors.gray[4],
                  }}
                >
                  준비 중
                </span>
              )}
            </div>
            <span
              className="mt-1"
              style={{
                fontSize: designTokens.typography.fontSize['body-small'][0],
                color: designTokens.colors.gray[4],
                lineHeight: '1.5',
              }}
            >
              {item.description}
            </span>
          </button>
        ))}
      </div>

      {/* 보고서 다운로드 모달 */}
      {activeModal === 'report' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetModal} />
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4"
            style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ borderBottomColor: designTokens.colors.gray[1] }}
            >
              <h2
                style={{
                  fontSize: designTokens.typography.fontSize['title-small'][0],
                  fontWeight: 700,
                  color: designTokens.colors.gray[6],
                }}
              >
                보고서 다운로드
              </h2>
              <button onClick={resetModal} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <label
                  className="block mb-2"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    fontWeight: 600,
                    color: designTokens.colors.gray[6],
                  }}
                >
                  사업 선택
                </label>
                <BusinessSelect
                  workspaceId={selectedWorkspace?.id || ''}
                  selectedBusinessId={selectedBusinessId}
                  selectedBusinessName={selectedBusinessName || '사업을 선택하세요'}
                  onSelect={handleBusinessSelect}
                  placeholder="사업을 선택하세요"
                />
              </div>

              {/* 보고서 유형 선택 (선택, 복수 가능) — 사업 선택 후 노출 */}
              {selectedBusinessId && (
                <div>
                  <label
                    className="block mb-2"
                    style={{
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      fontWeight: 600,
                      color: designTokens.colors.gray[6],
                    }}
                  >
                    보고서 유형 (복수 선택 가능)
                  </label>
                  {isTemplatesLoading ? (
                    <div
                      className="flex items-center gap-2 p-3 rounded"
                      style={{
                        backgroundColor: designTokens.colors.gray[0],
                        fontSize: designTokens.typography.fontSize['body-small'][0],
                        color: designTokens.colors.gray[5],
                      }}
                    >
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                      보고서 유형을 불러오는 중...
                    </div>
                  ) : templatesError ? (
                    <p
                      style={{
                        fontSize: designTokens.typography.fontSize['body-small'][0],
                        color: designTokens.colors.error.DEFAULT,
                      }}
                    >
                      {templatesError}
                    </p>
                  ) : reportTemplates.length === 0 ? (
                    <p
                      style={{
                        fontSize: designTokens.typography.fontSize['body-small'][0],
                        color: designTokens.colors.gray[4],
                      }}
                    >
                      선택 가능한 보고서 유형이 없습니다.
                    </p>
                  ) : (
                    <div
                      className="rounded border overflow-y-auto"
                      style={{ borderColor: designTokens.colors.gray[1], maxHeight: '180px' }}
                    >
                      {reportTemplates.map(template => (
                        <div
                          key={template.key}
                          className="px-3 py-2 border-b last:border-b-0"
                          style={{ borderBottomColor: designTokens.colors.gray[1] }}
                        >
                          <Checkbox
                            checked={selectedTemplateKeys.includes(template.key)}
                            onChange={() => toggleTemplateKey(template.key)}
                            disabled={isDownloading}
                            size="small"
                            label={template.name}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <p
                    className="mt-1"
                    style={{
                      fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                      color: designTokens.colors.gray[4],
                    }}
                  >
                    선택하지 않으면 활성화된 모든 보고서 유형을 다운로드합니다.
                  </p>
                </div>
              )}

              {/* 문서 형식 선택 — 기본 HWPX */}
              <div>
                <label
                  className="block mb-2"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    fontWeight: 600,
                    color: designTokens.colors.gray[6],
                  }}
                >
                  문서 형식
                </label>
                <div className="flex gap-2">
                  {(['hwpx', 'pdf'] as ReportFormat[]).map(format => (
                    <Button
                      key={format}
                      size="small"
                      color={reportFormat === format ? 'dark' : 'light'}
                      onClick={() => setReportFormat(format)}
                      disabled={isDownloading}
                    >
                      {format.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>

              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                  lineHeight: '1.5',
                }}
              >
                선택한 사업의 세부실행과제 보고서를 {reportFormat.toUpperCase()} 형식으로 생성하여 ZIP 파일로 다운로드합니다.
              </div>
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-2 px-6 py-4 border-t"
              style={{ borderTopColor: designTokens.colors.gray[1] }}
            >
              <Button size="medium" color="white" onClick={resetModal}>
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={handleReportDownload}
                disabled={!selectedBusinessId || isDownloading}
                className="flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                다운로드
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 증빙자료 다운로드 모달 */}
      {activeModal === 'attachment' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetModal} />
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4"
            style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ borderBottomColor: designTokens.colors.gray[1] }}
            >
              <h2
                style={{
                  fontSize: designTokens.typography.fontSize['title-small'][0],
                  fontWeight: 700,
                  color: designTokens.colors.gray[6],
                }}
              >
                증빙자료 다운로드
              </h2>
              <button onClick={resetModal} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              {/* 사업 선택 (항상 표시) */}
              <div>
                <label
                  className="block mb-2"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    fontWeight: 600,
                    color: designTokens.colors.gray[6],
                  }}
                >
                  사업 선택
                </label>
                <BusinessSelect
                  workspaceId={selectedWorkspace?.id || ''}
                  selectedBusinessId={selectedBusinessId}
                  selectedBusinessName={selectedBusinessName || '사업을 선택하세요'}
                  onSelect={handleBusinessSelect}
                  placeholder="사업을 선택하세요"
                />
              </div>

              {/* 다운로드 방식 선택 */}
              {selectedBusinessId && (
                <div>
                  <label
                    className="block mb-2"
                    style={{
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      fontWeight: 600,
                      color: designTokens.colors.gray[6],
                    }}
                  >
                    다운로드 방식
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="medium"
                      color={attachmentView === 'select' ? 'dark' : 'white'}
                      onClick={() => setAttachmentView('select')}
                      className="flex-1"
                    >
                      전체(일괄)
                    </Button>
                    <Button
                      size="medium"
                      color={attachmentView === 'period' ? 'dark' : 'white'}
                      onClick={() => setAttachmentView('period')}
                      className="flex-1"
                    >
                      기간 선택
                    </Button>
                  </div>
                </div>
              )}

              {/* 기간 입력 */}
              {selectedBusinessId && attachmentView === 'period' && (
                <div>
                  <label
                    className="block mb-2"
                    style={{
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      fontWeight: 600,
                      color: designTokens.colors.gray[6],
                    }}
                  >
                    다운로드 기간
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <DateInput
                        value={periodStartDate}
                        onChange={setPeriodStartDate}
                        placeholder="시작일"
                      />
                    </div>
                    <span style={{ color: designTokens.colors.gray[4] }}>~</span>
                    <div className="flex-1">
                      <DateInput
                        value={periodEndDate}
                        onChange={setPeriodEndDate}
                        placeholder="종료일"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                  lineHeight: '1.5',
                }}
              >
                선택한 사업의 모든 증빙자료 파일을 ZIP으로 압축하여 다운로드합니다.
              </div>
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-2 px-6 py-4 border-t"
              style={{ borderTopColor: designTokens.colors.gray[1] }}
            >
              <Button size="medium" color="white" onClick={resetModal}>
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={attachmentView === 'period' ? handleAttachmentDownloadPeriod : handleAttachmentDownloadAll}
                disabled={
                  !selectedBusinessId ||
                  isDownloading ||
                  (attachmentView === 'period' && (!periodStartDate || !periodEndDate))
                }
                className="flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                다운로드
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 과제/업무 다운로드 모달 */}
      {activeModal === 'task' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetModal} />
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4"
            style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ borderBottomColor: designTokens.colors.gray[1] }}
            >
              <h2
                style={{
                  fontSize: designTokens.typography.fontSize['title-small'][0],
                  fontWeight: 700,
                  color: designTokens.colors.gray[6],
                }}
              >
                과제/업무 다운로드
              </h2>
              <button onClick={resetModal} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                  lineHeight: '1.5',
                }}
              >
                현재 워크스페이스({selectedWorkspace?.name})의 전체 과제/업무 데이터를 엑셀 파일로 다운로드합니다.
              </div>
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-2 px-6 py-4 border-t"
              style={{ borderTopColor: designTokens.colors.gray[1] }}
            >
              <Button size="medium" color="white" onClick={resetModal}>
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={handleTaskExcelDownload}
                disabled={!selectedWorkspace?.id || isDownloading}
                className="flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                다운로드
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 성과지표 다운로드 모달 */}
      {activeModal === 'performance' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetModal} />
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4"
            style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
          >
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ borderBottomColor: designTokens.colors.gray[1] }}
            >
              <h2
                style={{
                  fontSize: designTokens.typography.fontSize['title-small'][0],
                  fontWeight: 700,
                  color: designTokens.colors.gray[6],
                }}
              >
                성과지표 다운로드
              </h2>
              <button onClick={resetModal} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                  lineHeight: '1.5',
                }}
              >
                현재 워크스페이스({selectedWorkspace?.name})의 전체 성과지표 데이터를 엑셀 파일로 다운로드합니다.
              </div>
            </div>

            <div
              className="flex items-center justify-end gap-2 px-6 py-4 border-t"
              style={{ borderTopColor: designTokens.colors.gray[1] }}
            >
              <Button size="medium" color="white" onClick={resetModal}>
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={handlePerformanceExcelDownload}
                disabled={!selectedWorkspace?.id || isDownloading}
                className="flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                다운로드
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 변경이력 다운로드 모달 */}
      {activeModal === 'history' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetModal} />
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4"
            style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
          >
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ borderBottomColor: designTokens.colors.gray[1] }}
            >
              <h2
                style={{
                  fontSize: designTokens.typography.fontSize['title-small'][0],
                  fontWeight: 700,
                  color: designTokens.colors.gray[6],
                }}
              >
                변경이력 다운로드
              </h2>
              <button onClick={resetModal} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                  lineHeight: '1.5',
                }}
              >
                현재 워크스페이스({selectedWorkspace?.name})의 전체 변경이력을 엑셀 파일로 다운로드합니다.
              </div>
            </div>

            <div
              className="flex items-center justify-end gap-2 px-6 py-4 border-t"
              style={{ borderTopColor: designTokens.colors.gray[1] }}
            >
              <Button size="medium" color="white" onClick={resetModal}>
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={handleHistoryExcelDownload}
                disabled={!selectedWorkspace?.id || isDownloading}
                className="flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                다운로드
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 회의록 다운로드 모달 */}
      {activeModal === 'article' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={resetModal} />
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4"
            style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
          >
            <div
              className="flex items-center justify-between px-6 py-4 border-b"
              style={{ borderBottomColor: designTokens.colors.gray[1] }}
            >
              <h2
                style={{
                  fontSize: designTokens.typography.fontSize['title-small'][0],
                  fontWeight: 700,
                  color: designTokens.colors.gray[6],
                }}
              >
                회의록 다운로드
              </h2>
              <button onClick={resetModal} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.gray[5],
                  lineHeight: '1.5',
                }}
              >
                현재 워크스페이스({selectedWorkspace?.name})의 전체 회의록을 엑셀 파일로 다운로드합니다.
              </div>
            </div>

            <div
              className="flex items-center justify-end gap-2 px-6 py-4 border-t"
              style={{ borderTopColor: designTokens.colors.gray[1] }}
            >
              <Button size="medium" color="white" onClick={resetModal}>
                취소
              </Button>
              <Button
                size="medium"
                color="dark"
                onClick={handleArticleExcelDownload}
                disabled={!selectedWorkspace?.id || isDownloading}
                className="flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                다운로드
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
